import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LockKeyhole } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { homePath } from "@/lib/roles";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Login() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = mode === "login"
        ? await login(form.email, form.password)
        : await register(form.name, form.email, form.password);
      toast.success(`Welcome, ${user.name}`);
      navigate(homePath(user));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-5 py-20" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="border border-line bg-graphite p-10">
          <div className="w-12 h-12 bg-ember/15 border border-ember/40 flex items-center justify-center mb-8">
            <LockKeyhole size={20} className="text-ember" />
          </div>
          <h1 className="font-heading text-2xl uppercase tracking-tight text-white">
            {mode === "login" ? "ERP & Portal Login" : "Create Account"}
          </h1>
          <p className="text-silver text-sm mt-2">
            {mode === "login" ? "Access the OGK ERP, client portal and admin CMS." : "Register as a client to follow your project."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            {mode === "register" && (
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Full Name</label>
                <Input required value={form.name} onChange={set("name")} placeholder="Your name" data-testid="register-name-input" className="rounded-none bg-carbon border-line h-12" />
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Email</label>
              <Input required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="login-email-input" className="rounded-none bg-carbon border-line h-12" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Password</label>
              <Input required type="password" value={form.password} onChange={set("password")} placeholder="••••••••" data-testid="login-password-input" className="rounded-none bg-carbon border-line h-12" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-btn"
              className="w-full rounded-none bg-ember hover:bg-[#e64a19] text-white uppercase tracking-[0.15em] font-bold h-13 h-12 text-sm">
              {loading ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
            </Button>
          </form>

          <button onClick={() => setMode(mode === "login" ? "register" : "login")} data-testid="login-mode-toggle"
            className="mt-6 text-silver text-xs uppercase tracking-[0.15em] hover:text-ember transition-colors duration-200">
            {mode === "login" ? "New client? Create an account" : "Already registered? Sign in"}
          </button>

          <div className="flex items-center gap-4 my-6">
            <span className="h-px bg-line flex-1" />
            <span className="text-silver/70 text-[10px] uppercase tracking-[0.25em]">or</span>
            <span className="h-px bg-line flex-1" />
          </div>

          <button onClick={googleLogin} data-testid="google-login-btn"
            className="w-full border border-line bg-carbon text-white uppercase tracking-[0.15em] font-bold h-12 text-sm flex items-center justify-center gap-3 hover:border-ember transition-colors duration-200">
            <svg width="17" height="17" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52Z"/></svg>
            Continue with Google
          </button>
        </div>
        <p className="text-silver/60 text-xs text-center mt-6 uppercase tracking-widest">Protected by JWT authentication & rate limiting</p>
      </div>
    </div>
  );
}
